import { PROMO_OBJECT } from "../pages/promotions/constants";
import { deepClone } from "../constants/utils";
import {
  filterCombosForProperty,
  normalizePropertyKey,
} from "../components/create-promo/utils/roomRateCombos";

// ── Price type mappings ──

const PRICE_TYPE_TO_DISCOUNT = {
  PERCENT: 2,
  FLAT: 1,
};

const DISCOUNT_TO_PRICE_TYPE = {
  2: "PERCENT",
  1: "FLAT",
};

const PRICE_CALCULATION_TO_CALC_TYPE = {
  ADULTS_ONLY: 2,
  BASE_PRICE: 0,
  TOTAL: 1,
};

const CALC_TYPE_TO_PRICE_CALCULATION = {
  2: "ADULTS_ONLY",
  0: "BASE_PRICE",
  1: "TOTAL",
};

const PRICE_ADJUSTMENT_TO_OPERATION = {
  INCREASE: "ADDITION",
  DECREASE: "SUBTRACTION",
};

const PRICE_OPERATION_TO_ADJUSTMENT = {
  ADDITION: "INCREASE",
  SUBTRACTION: "DECREASE",
};

// ── List Mapper ──

/**
 * Map derived promo list API response to flat promo array for PromotionsTable.
 * NEW API shape: [{ promoId, name, status, derivedRateCount }]
 * Target shape: [{ id, name, status, discountValue, discountType, stayDates, bookingWindow, created, revenue, ... }]
 */
export function mapDerivedPromoListToPromoList(apiResponse) {
  // NEW API returns a direct array; support both wrapped and unwrapped forms
  const promos = Array.isArray(apiResponse)
    ? apiResponse
    : Array.isArray(apiResponse?.data)
      ? apiResponse.data
      : [];

  return promos.map((promo) => {
    const derivedRateCount = promo.derivedRateCount || 0;

    return {
      id: promo.promoId,
      promoId: promo.promoId,
      campaignId: null,
      name: promo.name,
      status: mapStatus(promo.status),
      promoType: "Custom",
      creationSource: promo.creationSource || null,
      isGcCreated: promo.creationSource === "GC_RATEPLAN_SCREEN",
      // Discount info: not in list response, show placeholder
      discountValue: null,
      discountType: null,
      discountMap: null,
      isDoW: false,
      isStacked: derivedRateCount > 1,
      // Dates: not in list response, show N/A
      stayDates: { start: null, end: null },
      bookingWindow: { start: null, end: null },
      created: promo.createdAt || "",
      revenue: 0,
      derivedRatesCount: derivedRateCount,
    };
  });
}

function mapStatus(apiStatus) {
  if (apiStatus === "ACTIVE") return "Active";
  if (apiStatus === "INACTIVE") return "Inactive";
  if (apiStatus === "DRAFT") return "Draft";
  return apiStatus;
}

// ── Shared helper ──

function buildDiscountVO(pc) {
  const discountVO = deepClone(PROMO_OBJECT.rateRule.ruleVO.discountVO);
  const mCompDiscountType = PRICE_TYPE_TO_DISCOUNT[pc.priceType] || 2;
  discountVO.mCompDiscountType = mCompDiscountType;
  discountVO.priceOperation = pc.priceOperation || "SUBTRACTION";
  discountVO.priceAdjustment =
    PRICE_OPERATION_TO_ADJUSTMENT[discountVO.priceOperation] || "DECREASE";
  discountVO.order = pc.order ?? null;
  discountVO.derivedFromId = pc.derivedFromId ?? null;
  discountVO.calculationType =
    PRICE_CALCULATION_TO_CALC_TYPE[pc.priceCalculation] ?? 0;

  const priceDelta = pc.priceDelta || {};
  const deltaKeys = Object.keys(priceDelta);
  const deltaValues = Object.values(priceDelta);
  const allSevenDays = deltaKeys.length === 7;
  const allSame =
    deltaValues.length > 0 && deltaValues.every((v) => v === deltaValues[0]);

  // Only treat as "flat" (all days same value) if all 7 days are present and values are equal
  if (allSevenDays && allSame) {
    discountVO.discountType = 4; // flat
    if (mCompDiscountType === 2) {
      discountVO.percentDiscountAmount = deltaValues[0];
    } else {
      discountVO.fixedDiscountAmount = deltaValues[0];
    }
  } else {
    discountVO.discountType = 1; // day-wise
  }

  // Build full 7-day map, defaulting missing days to 0
  const fullMap = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 };
  for (const [key, value] of Object.entries(priceDelta)) {
    fullMap[key] = Number(value) || 0;
  }
  if (mCompDiscountType === 2) {
    discountVO.percentDiscountMap = fullMap;
  } else {
    discountVO.fixedDiscountMap = fullMap;
  }

  // Store which days were actually selected (have non-zero or present values)
  discountVO.appliedDaysOfWeek = deltaKeys.map(Number);

  return discountVO;
}

// ── Detail Mapper: API → PROMO_OBJECT ──

/**
 * Map promo detail API response to PROMO_OBJECT shape for CreatePromo component.
 * API shape: { meta: {...}, promoConfig: { selectedRateplans, priceConfig: [...], restrictionConfig, contractConfig, distributionConfig } }
 */
export function mapDerivedPromoDetailToPromo(detail) {
  const promo = deepClone(PROMO_OBJECT);
  const meta = detail?.meta || {};
  const promoConfig = detail?.promoConfig || {};

  // Name
  promo.rateRule.ruleVO.descriptionVO.name = meta.name || "";

  // Price config → discountVO
  const isGcCreated = meta.creationSource === "GC_RATEPLAN_SCREEN";
  const gcPriceConfigs = isGcCreated
    ? detail?.derivedRates?.[0]?.priceConfig || []
    : [];

  if (isGcCreated && gcPriceConfigs.length > 0) {
    promo.rateRule.ruleVO.discountVO = gcPriceConfigs.map((pc) =>
      buildDiscountVO(pc)
    );
  } else if (promoConfig.priceConfig?.length > 0) {
    promo.rateRule.ruleVO.discountVO = buildDiscountVO(
      promoConfig.priceConfig[0]
    );
  }

  // Restriction config → bookingVO + stayVO
  const rc = promoConfig.restrictionConfig;
  if (rc) {
    // Booking window
    if (rc.bookDates?.length > 0) {
      promo.rateRule.ruleVO.bookingVO.bookingStartDate =
        rc.bookDates[0].startDate;
      promo.rateRule.ruleVO.bookingVO.bookingEndDate = rc.bookDates[0].endDate;
    }

    // Lead time
    promo.rateRule.ruleVO.bookingVO.minBookingLeadTime = rc.minLeadTime ?? null;
    promo.rateRule.ruleVO.bookingVO.maxBookingLeadTime = rc.maxLeadTime ?? null;

    // Stay dates (EO-1492 multi-range). Keep the legacy single fields on the
    // first range for backward compat, and populate stayDateRanges so the form
    // shows every stored range on edit.
    if (rc.stayDates?.length > 0) {
      promo.rateRule.ruleVO.stayVO.stayStartDate = rc.stayDates[0].startDate;
      promo.rateRule.ruleVO.stayVO.stayEndDate = rc.stayDates[0].endDate;
      promo.rateRule.ruleVO.stayVO.stayDateRanges = rc.stayDates.map((sd) => ({
        stayStartDate: sd.startDate,
        stayEndDate: sd.endDate,
      }));
    }

    // LOS
    promo.rateRule.ruleVO.stayVO.minLengthOfStay = rc.minLos ?? null;
    promo.rateRule.ruleVO.stayVO.maxLengthOfStay = rc.maxLos ?? null;

    // Blackout dates
    if (rc.blackOutDates?.length > 0) {
      promo.rateRule.ruleVO.stayVO.blackOutDateRanges = rc.blackOutDates.map(
        (bod) => ({
          blackOutStartDate: bod.startDate,
          blackOutEndDate: bod.endDate,
          blackOutStayThruEnabled: bod.allowStayThrough ?? false,
        })
      );
    }

    // DOW
    if (rc.arrivalDow?.length > 0 && rc.arrivalDow.length < 7) {
      promo.rateRule.ruleVO.stayVO.mustArriveDaysOfWeek = rc.arrivalDow;
    }
  }

  // Selected rateplans/roomtypes → allowedRateRoomCodes + allowedRateRoomCodesByProperty
  if (promoConfig?.rateRoomSelections?.length > 0) {
    const allCombos = [];
    const byProperty = {};
    promoConfig.rateRoomSelections.forEach((selection) => {
      const propId = selection.propertyId;
      const combos = (selection.combinations || []).flatMap((combo) =>
        (combo.roomtypeCodes || []).map((roomCode, index) => ({
          mLeft: combo.baseRateplanCode,
          mRight: roomCode,
          rateplanId: combo.baseRateplanId,
          roomtypeId: combo.roomtypeIds?.[index],
          propertyId: normalizePropertyKey(propId),
        }))
      );
      allCombos.push(...combos);
      if (propId) {
        byProperty[propId] = combos;
      }
    });
    promo.allowedRateRoomCodes = allCombos;
    promo.allowedRateRoomCodesByProperty = byProperty;
    promo.selectedProperties = promoConfig.rateRoomSelections
      .map((s) => s.propertyId)
      .filter(Boolean);
  }

  // Attach metadata for reference
  promo._derivedRateMeta = {
    promoId: meta.promoId,
    campaignId: meta.campaignId,
    // CUSTOM / SMART / BUYER_CAMPAIGN — what isCampaignPromo reads after a detail fetch.
    promoType: meta.promoType || null,
    propertyId: detail?.propertyId || meta.propertyId || null,
    status: meta.status,
    creationSource: meta.creationSource,
    isGcCreated: meta.creationSource === "GC_RATEPLAN_SCREEN",
    priceConfigs: promoConfig?.priceConfig || [],
    distributionConfig: promoConfig?.distributionConfig || null,
    contractConfig: promoConfig?.contractConfig || null,
  };

  return promo;
}

// ── Create Mapper: PROMO_OBJECT → API request ──

/**
 * Convert all values in a priceDelta object to strings.
 */
function stringifyPriceDelta(priceDelta) {
  const result = {};
  for (const [key, value] of Object.entries(priceDelta)) {
    result[key] = String(value);
  }
  return result;
}

/**
 * Group room/rate plan combos into API `combinations`, keeping only the combos
 * that belong to `propertyId`. Rate plan ids are property-scoped, so a combo
 * carrying a different property's tag would otherwise be sent as if it were
 * this property's rate plan.
 */
function buildCombinations(rateRoomCodes, propertyId) {
  const groupedByRateplan = {};
  filterCombosForProperty(rateRoomCodes, propertyId).forEach((arc) => {
    const key = arc.rateplanId;
    if (!groupedByRateplan[key]) {
      groupedByRateplan[key] = {
        baseRateplanId: arc.rateplanId,
        baseRateplanCode: arc.mLeft,
        derivedFrom: arc.rateplanId,
        derivedFromType: "BASE_RATEPLAN",
        roomtypeIds: [],
        roomtypeCodes: [],
      };
    }
    groupedByRateplan[key].roomtypeIds.push(arc.roomtypeId);
    groupedByRateplan[key].roomtypeCodes.push(arc.mRight);
  });
  return Object.values(groupedByRateplan);
}

/**
 * Arrival day-of-week portion of a `restrictionConfig`, as a spreadable object.
 *
 * EO-1134: omit DOW rather than defaulting to [1..7] — all-days is semantically
 * empty ("always allow"), and sending it makes the backend mark restrictions as
 * OVERRIDDEN. A genuine arrival-DOW subset must still be sent, since the backend
 * replaces the whole stored restrictionConfig on update. `stayDow` is never sent:
 * no screen collects it.
 */
function buildArrivalDowRestriction(stayVO) {
  return stayVO.mustArriveDaysOfWeek?.length > 0
    ? { arrivalDow: stayVO.mustArriveDaysOfWeek }
    : {};
}

/**
 * Build the `restrictionConfig.stayDates` array (EO-1492 multi-range).
 *
 * Prefers `stayVO.stayDateRanges` (the multi-range array written by the form)
 * so every selected range is sent, falling back to the legacy single
 * stayStartDate/stayEndDate pair for promos that predate multi-range.
 */
function buildStayDatesForApi(stayVO) {
  const ranges = stayVO?.stayDateRanges;
  if (Array.isArray(ranges) && ranges.length > 0) {
    return ranges
      .filter((r) => r.stayStartDate && r.stayEndDate)
      .map((r) => ({ startDate: r.stayStartDate, endDate: r.stayEndDate }));
  }
  if (stayVO?.stayStartDate && stayVO?.stayEndDate) {
    return [{ startDate: stayVO.stayStartDate, endDate: stayVO.stayEndDate }];
  }
  return [];
}

/**
 * Map form state to Create Derived Promo API request body.
 */
export function mapPromoToCreateDerivedPayload(
  promo,
  selectedPropertyIds,
  calculationType,
  isDraft
) {
  const discountVO = promo.rateRule.ruleVO.discountVO;
  const bookingVO = promo.rateRule.ruleVO.bookingVO;
  const stayVO = promo.rateRule.ruleVO.stayVO;

  // Build priceDelta from discount maps (values must be strings)
  const mCompDiscountType = discountVO.mCompDiscountType;
  let priceDelta = {};
  if (discountVO.discountType === 1) {
    // Day-wise
    priceDelta =
      mCompDiscountType === 2
        ? { ...discountVO.percentDiscountMap }
        : { ...discountVO.fixedDiscountMap };
  } else {
    // Flat - apply same value to all days
    const value =
      mCompDiscountType === 2
        ? discountVO.percentDiscountAmount || 0
        : discountVO.fixedDiscountAmount || 0;
    priceDelta = {
      1: value,
      2: value,
      3: value,
      4: value,
      5: value,
      6: value,
      7: value,
    };
  }
  priceDelta = stringifyPriceDelta(priceDelta);

  // Build rateRoomSelections grouped by rateplanId
  let rateRoomSelections;
  if (selectedPropertyIds.length > 1 && promo.allowedRateRoomCodesByProperty) {
    rateRoomSelections = selectedPropertyIds.map((propertyId) => ({
      propertyId,
      combinations: buildCombinations(
        promo.allowedRateRoomCodesByProperty[propertyId],
        propertyId
      ),
    }));
  } else {
    rateRoomSelections = selectedPropertyIds.map((propertyId) => ({
      propertyId,
      combinations: buildCombinations(promo.allowedRateRoomCodes, propertyId),
    }));
  }

  // Build restriction config — only include if user set any restriction values
  const hasRestrictions =
    bookingVO.bookingStartDate ||
    bookingVO.bookingEndDate ||
    stayVO.stayStartDate ||
    stayVO.stayEndDate ||
    (stayVO.stayDateRanges && stayVO.stayDateRanges.length > 0) ||
    (stayVO.blackOutDateRanges && stayVO.blackOutDateRanges.length > 0) ||
    stayVO.minLengthOfStay ||
    stayVO.maxLengthOfStay ||
    bookingVO.minBookingLeadTime ||
    bookingVO.maxBookingLeadTime ||
    (stayVO.mustArriveDaysOfWeek && stayVO.mustArriveDaysOfWeek.length > 0);

  const restrictionConfig = hasRestrictions
    ? {
        bookDates:
          bookingVO.bookingStartDate && bookingVO.bookingEndDate
            ? [
                {
                  startDate: bookingVO.bookingStartDate,
                  endDate: bookingVO.bookingEndDate,
                },
              ]
            : [],
        stayDates: buildStayDatesForApi(stayVO),
        blackOutDates: (stayVO.blackOutDateRanges || []).map((bod) => ({
          startDate: bod.blackOutStartDate,
          endDate: bod.blackOutEndDate,
          allowStayThrough: bod.blackOutStayThruEnabled ?? false,
        })),
        minLos: stayVO.minLengthOfStay || undefined,
        maxLos: stayVO.maxLengthOfStay || undefined,
        minLeadTime: bookingVO.minBookingLeadTime || undefined,
        maxLeadTime: bookingVO.maxBookingLeadTime || undefined,
        ...buildArrivalDowRestriction(stayVO),
      }
    : undefined;

  const payload = {
    name: promo.rateRule.ruleVO.descriptionVO.name,
    status: isDraft ? "DRAFT" : "ACTIVE",
    rateRoomSelections,
    priceConfig: [
      {
        priceType: DISCOUNT_TO_PRICE_TYPE[mCompDiscountType] || "PERCENT",
        priceCalculation:
          CALC_TYPE_TO_PRICE_CALCULATION[calculationType] || "BASE_PRICE",
        priceDelta,
        priceOperation:
          PRICE_ADJUSTMENT_TO_OPERATION[discountVO.priceAdjustment] ||
          "SUBTRACTION",
      },
    ],
    contractConfig: {
      mealPlanCode: null,
      cancellationPolicy: { windows: [] },
    },
    commercialModel: "COMMUNITY",
    promoType: promo.promoType || "CUSTOM",
  };

  if (promo.suggestionPromoId) {
    payload.suggestionPromoId = promo.suggestionPromoId;
  }
  if (promo.promoCategory) {
    payload.promoCategory = promo.promoCategory;
  }

  if (restrictionConfig) {
    payload.restrictionConfig = restrictionConfig;
  }
  // distributionConfig not included in create — SP users cannot set distribution
  return payload;
}

// ── Update Mapper: PROMO_OBJECT → API request ──

/**
 * Map form state to Update Derived Promo API request body.
 */
export function mapPromoToUpdateDerivedPayload(promo, calculationType) {
  const discountVO = promo.rateRule.ruleVO.discountVO;
  const bookingVO = promo.rateRule.ruleVO.bookingVO;
  const stayVO = promo.rateRule.ruleVO.stayVO;

  const mCompDiscountType = discountVO.mCompDiscountType;
  let priceDelta = {};
  if (discountVO.discountType === 1) {
    priceDelta =
      mCompDiscountType === 2
        ? { ...discountVO.percentDiscountMap }
        : { ...discountVO.fixedDiscountMap };
  } else {
    const value =
      mCompDiscountType === 2
        ? discountVO.percentDiscountAmount || 0
        : discountVO.fixedDiscountAmount || 0;
    priceDelta = {
      1: value,
      2: value,
      3: value,
      4: value,
      5: value,
      6: value,
      7: value,
    };
  }
  priceDelta = stringifyPriceDelta(priceDelta);

  // Build rateRoomSelections grouped by propertyId
  let rateRoomSelections = [];
  if (promo.allowedRateRoomCodesByProperty) {
    // Multi-property: one entry per propertyId
    rateRoomSelections = Object.entries(
      promo.allowedRateRoomCodesByProperty
    ).map(([propId, codes]) => ({
      propertyId: Number(propId),
      combinations: buildCombinations(codes, propId),
    }));
  } else {
    // Single property fallback
    const propertyId = promo._derivedRateMeta?.propertyId;
    const combinations = buildCombinations(
      promo.allowedRateRoomCodes,
      propertyId
    );
    if (propertyId) {
      rateRoomSelections = [{ propertyId: Number(propertyId), combinations }];
    } else if (combinations.length > 0) {
      rateRoomSelections = [{ propertyId: null, combinations }];
    }
  }

  // Build restriction config — only include if user set any restriction values
  const hasRestrictions =
    bookingVO.bookingStartDate ||
    bookingVO.bookingEndDate ||
    stayVO.stayStartDate ||
    stayVO.stayEndDate ||
    (stayVO.stayDateRanges && stayVO.stayDateRanges.length > 0) ||
    (stayVO.blackOutDateRanges && stayVO.blackOutDateRanges.length > 0) ||
    stayVO.minLengthOfStay ||
    stayVO.maxLengthOfStay ||
    bookingVO.minBookingLeadTime ||
    bookingVO.maxBookingLeadTime ||
    (stayVO.mustArriveDaysOfWeek && stayVO.mustArriveDaysOfWeek.length > 0);

  const restrictionConfig = hasRestrictions
    ? {
        bookDates:
          bookingVO.bookingStartDate && bookingVO.bookingEndDate
            ? [
                {
                  startDate: bookingVO.bookingStartDate,
                  endDate: bookingVO.bookingEndDate,
                },
              ]
            : [],
        stayDates: buildStayDatesForApi(stayVO),
        blackOutDates: (stayVO.blackOutDateRanges || []).map((bod) => ({
          startDate: bod.blackOutStartDate,
          endDate: bod.blackOutEndDate,
          allowStayThrough: bod.blackOutStayThruEnabled ?? false,
        })),
        minLos: stayVO.minLengthOfStay || undefined,
        maxLos: stayVO.maxLengthOfStay || undefined,
        minLeadTime: bookingVO.minBookingLeadTime || undefined,
        maxLeadTime: bookingVO.maxBookingLeadTime || undefined,
        ...buildArrivalDowRestriction(stayVO),
      }
    : undefined;

  // distributionConfig: preserve from detail response if it was set, otherwise omit
  const existingDistribution = promo._derivedRateMeta?.distributionConfig;

  const payload = {
    name: promo.rateRule.ruleVO.descriptionVO.name,
    status: promo._derivedRateMeta?.status || "ACTIVE",
    rateRoomSelections,
    priceConfig: [
      {
        priceType: DISCOUNT_TO_PRICE_TYPE[mCompDiscountType] || "PERCENT",
        priceCalculation:
          CALC_TYPE_TO_PRICE_CALCULATION[calculationType] || "BASE_PRICE",
        priceDelta,
        priceOperation:
          PRICE_ADJUSTMENT_TO_OPERATION[discountVO.priceAdjustment] ||
          "SUBTRACTION",
      },
    ],
    contractConfig: {
      mealPlanCode: null,
      cancellationPolicy: { windows: [] },
    },
  };

  payload.promoType = promo.promoType || "CUSTOM";
  if (promo.suggestionPromoId) {
    payload.suggestionPromoId = promo.suggestionPromoId;
  }
  if (promo.promoCategory) {
    payload.promoCategory = promo.promoCategory;
  }

  if (restrictionConfig) {
    payload.restrictionConfig = restrictionConfig;
  }
  if (existingDistribution) {
    payload.distributionConfig = existingDistribution;
  }
  return payload;
}

/**
 * Extract calculationType from a promo detail's priceConfig.
 */
export function extractCalculationType(promoDetail) {
  // The child's own priceConfig has no derivedFromId. Inherited modifiers from
  // parents carry derivedFromId set to the parent's ID. When a derived rate is
  // stacked, promoConfig.priceConfig reflects the merged/parent calculation
  // (e.g. the first stacked entry), so the child's own calculation must be read
  // from derivedRates[].priceConfig instead. Prefer the own config with the
  // highest order in case of multiple non-inherited entries.
  const drPriceConfigs = promoDetail?.derivedRates?.[0]?.priceConfig;
  if (Array.isArray(drPriceConfigs) && drPriceConfigs.length > 0) {
    const ownConfig = drPriceConfigs
      .filter((pc) => !pc.derivedFromId)
      .sort((a, b) => (b.order ?? 0) - (a.order ?? 0))[0];
    if (ownConfig?.priceCalculation) {
      return PRICE_CALCULATION_TO_CALC_TYPE[ownConfig.priceCalculation] ?? 0;
    }
  }

  const fallbackConfig = promoDetail?.promoConfig?.priceConfig;
  const priceCalc =
    (Array.isArray(fallbackConfig)
      ? fallbackConfig[0]?.priceCalculation
      : fallbackConfig?.priceCalculation) || "BASE_PRICE";
  return PRICE_CALCULATION_TO_CALC_TYPE[priceCalc] ?? 0;
}
